import { ACADEMY_CATALOG_SERVICE_URI, BASE_URI_PROGRESS } from '../../constants/ApiConstants';
import http from 'hub-http/clients/apiClient';
import { registerMutation, registerQuery, useMutation, useQuery } from 'academy-common-lib/utils/DataFetchingClient';
import { deleteContentProgressFromCache, useSetContentProgressStatusCache } from '../userProgress';
import { useFullLesson } from '../lessons/lessonQueries';
import { useCallback, useMemo } from 'react';
import PortalIdParser from 'PortalIdParser';
import enviro from 'enviro';
import { CREATE_PORTAL_MUTATION } from '../../../__generated__/chirp/com/hubspot/academy/simulation/environment/rpc/PortalService.dfc';
import Raven from 'raven-js';
import { useHasGate } from '../accounts';
const EXERCISE_SUBMISSIONS_URI = `${BASE_URI_PROGRESS}/exercise-submissions`;
const GET_PRACTICAL_EXERCISE_REQUIREMENTS = registerQuery({
  fieldName: 'practicalExerciseRequirements',
  args: ['contentId'],
  fetcher({
    contentId
  }) {
    return http.get(`${ACADEMY_CATALOG_SERVICE_URI}/exercises/${contentId}/requirements`);
  }
});
export const usePracticalExerciseRequirements = contentId => {
  const {
    data: practicalExerciseRequirements,
    loading: practicalExerciseRequirementsLoading,
    error: practicalExerciseRequirementsError
  } = useQuery(GET_PRACTICAL_EXERCISE_REQUIREMENTS, {
    variables: {
      contentId
    }
  });
  return {
    practicalExerciseRequirements,
    practicalExerciseRequirementsLoading,
    practicalExerciseRequirementsError
  };
};
export const usePracticalExercise = (lessonId, contentId, language) => {
  const {
    lesson,
    lessonLoading,
    lessonError
  } = useFullLesson(lessonId, language);
  const practicalExercise = useMemo(() => {
    if (!lesson || !lesson.content) return null;
    const foundExercise = lesson.content.find(content => content.id === contentId);
    return foundExercise;
  }, [contentId, lesson]);
  return {
    practicalExercise,
    practicalExerciseLoading: lessonLoading,
    practicalExerciseError: lessonError
  };
};
const START_PRACTICAL_EXERCISE = registerMutation({
  fieldName: 'setPracticalExerciseStarted',
  args: ['contentId', 'trackId', 'lessonId', 'simulationPortalId'],
  fetcher({
    contentId,
    lessonId,
    trackId,
    simulationPortalId
  }) {
    return http.post(`${BASE_URI_PROGRESS}/exercise-submissions/${contentId}/asset/start`, {
      data: {
        trackId,
        lessonId,
        simulationPortalId
      }
    });
  }
});
export const useSetPracticalExerciseStarted = language => {
  const [mutate, {
    loading: startPracticalExerciseLoading,
    called: startPracticalExerciseCalled,
    error: startPracticalExerciseError
  }] = useMutation(START_PRACTICAL_EXERCISE);
  const {
    setContentProgressStatusCache
  } = useSetContentProgressStatusCache({
    language
  });
  const startExercise = useCallback((trackId, lessonId, contentId, simulationPortalId) => {
    return mutate({
      variables: {
        contentId,
        lessonId,
        trackId,
        simulationPortalId
      },
      update() {
        setContentProgressStatusCache(contentId, 'STARTED', language);
      }
    }).then(result => result.data);
  }, [language, mutate, setContentProgressStatusCache]);
  return [startExercise, {
    startPracticalExerciseLoading,
    startPracticalExerciseCalled,
    startPracticalExerciseError
  }];
};
const DELETE_PRACTICAL_EXERCISE_PROGRESS = registerMutation({
  fieldName: 'deletePracticalExerciseProgress',
  args: ['contentId'],
  fetcher({
    contentId
  }) {
    return http.delete(`${BASE_URI_PROGRESS}/user-progress/exercise/${contentId}`);
  }
});
export const useDeletePracticalExerciseProgress = () => {
  const [mutate, {
    loading: deletePracticalExerciseProgressLoading,
    called: deletePracticalExerciseProgressCalled,
    error: deletePracticalExerciseProgressError
  }] = useMutation(DELETE_PRACTICAL_EXERCISE_PROGRESS);
  const deleteExerciseProgress = useCallback(contentId => {
    return mutate({
      variables: {
        contentId
      },
      update() {
        deleteContentProgressFromCache(contentId);
      }
    }).then(response => response.data);
  }, [mutate]);
  return [deleteExerciseProgress, {
    deletePracticalExerciseProgressLoading,
    deletePracticalExerciseProgressCalled,
    deletePracticalExerciseProgressError
  }];
};
const SUBMIT_MANUALLY_GRADED_PRACTICAL_EXERCISE = registerMutation({
  fieldName: 'submitManuallyGradedPracticalExercise',
  args: ['contentId', 'language', 'trackId', 'lessonId'],
  fetcher({
    contentId,
    language,
    trackId,
    lessonId
  }) {
    return http.post(`${BASE_URI_PROGRESS}/exercise-submissions/${contentId}/asset/manual`, {
      data: {
        trackId,
        lessonId,
        language
      }
    });
  }
});
export const useSetManuallyGradedPracticalExerciseSubmitted = language => {
  const [mutate, {
    loading: submitManuallyGradedPracticalExerciseLoading,
    called: submitManuallyGradedPracticalExerciseCalled,
    error: submitManuallyGradedPracticalExerciseError
  }] = useMutation(SUBMIT_MANUALLY_GRADED_PRACTICAL_EXERCISE);
  const {
    setContentProgressStatusCache
  } = useSetContentProgressStatusCache({
    language
  });
  const submitManuallyGradedExercise = useCallback((contentId, currentLanguage, trackId, lessonId) => {
    return mutate({
      variables: {
        contentId,
        language: currentLanguage,
        trackId,
        lessonId
      },
      update() {
        setContentProgressStatusCache(contentId, 'IN_REVIEW', language);
      }
    }).then(response => response.data);
  }, [mutate, setContentProgressStatusCache, language]);
  return [submitManuallyGradedExercise, {
    submitManuallyGradedPracticalExerciseLoading,
    submitManuallyGradedPracticalExerciseCalled,
    submitManuallyGradedPracticalExerciseError
  }];
};
const SUBMIT_AUTOMATICALLY_GRADED_PRACTICAL_EXERCISE = registerMutation({
  fieldName: 'submitAutomaticallyGradedPracticalExercise',
  args: ['contentId', 'simulationPortalId'],
  fetcher({
    contentId,
    simulationPortalId
  }) {
    return http.post(`${EXERCISE_SUBMISSIONS_URI}/${contentId}/asset/automatic`, {
      data: {
        simulationPortalId
      }
    });
  }
});
export const useSetAutomaticallyGradedPracticalExerciseSubmitted = language => {
  const [mutate, {
    loading: submitAutomaticallyGradedPracticalExerciseLoading,
    called: submitAutomaticallyGradedPracticalExerciseCalled,
    error: submitAutomaticallyGradedPracticalExerciseError
  }] = useMutation(SUBMIT_AUTOMATICALLY_GRADED_PRACTICAL_EXERCISE);
  const {
    setContentProgressStatusCache
  } = useSetContentProgressStatusCache({
    language
  });
  const submitAutomaticallyGradedExercise = useCallback((contentId, simulationPortalId) => {
    return mutate({
      variables: {
        contentId,
        simulationPortalId
      },
      update() {
        setContentProgressStatusCache(contentId, 'IN_REVIEW', language);
      }
    }).then(response => response.data);
  }, [mutate, setContentProgressStatusCache, language]);
  return [submitAutomaticallyGradedExercise, {
    submitAutomaticallyGradedPracticalExerciseLoading,
    submitAutomaticallyGradedPracticalExerciseCalled,
    submitAutomaticallyGradedPracticalExerciseError
  }];
};
const SUBMIT_MANUALLY_GRADED_PRACTICAL_EXERCISE_WITH_ASSET = registerMutation({
  fieldName: 'submitManuallyGradedPracticalExerciseWithAsset',
  args: ['contentId', 'assetType', 'reference'],
  fetcher({
    contentId,
    assetType,
    reference
  }) {
    return http.post(`${EXERCISE_SUBMISSIONS_URI}/${contentId}/manual-with-asset`, {
      data: {
        assetType,
        reference
      }
    });
  }
});
export const useSetManuallyGradedPracticalExerciseWithAssetSubmitted = language => {
  const [mutate, {
    loading: submitManuallyGradedPracticalExerciseWithAssetLoading,
    called: submitManuallyGradedPracticalExerciseWithAssetCalled,
    error: submitManuallyGradedPracticalExerciseWithAssetError
  }] = useMutation(SUBMIT_MANUALLY_GRADED_PRACTICAL_EXERCISE_WITH_ASSET);
  const {
    setContentProgressStatusCache
  } = useSetContentProgressStatusCache({
    language
  });
  const submitPracticalExerciseWithAsset = useCallback((contentId, assetType, reference) => {
    return mutate({
      variables: {
        contentId,
        assetType,
        reference
      },
      update() {
        setContentProgressStatusCache(contentId, 'IN_REVIEW', language);
      }
    }).then(response => response.data);
  }, [mutate, setContentProgressStatusCache, language]);
  return [submitPracticalExerciseWithAsset, {
    submitManuallyGradedPracticalExerciseWithAssetLoading,
    submitManuallyGradedPracticalExerciseWithAssetCalled,
    submitManuallyGradedPracticalExerciseWithAssetError
  }];
};
export const usePracticalExerciseSimulationPortal = ({
  variables
}) => {
  const [mutate, {
    data: simulationPortal,
    loading: simulationPortalLoading,
    error: simulationPortalError
  }] = useMutation(CREATE_PORTAL_MUTATION, {
    variables
  });
  return {
    getOrCreateSimulationPortal: mutate,
    simulationPortal,
    simulationPortalLoading,
    simulationPortalError
  };
};

// Track IDs that are fully enabled (no gate)
const ENABLED_SIMULATION_TRACKS = {
  qa: [35515440],
  // Sales Hub Cert
  prod: [9147486] // Sales Hub Cert
};
const MARKETING_HUB_TRACKS = {
  qa: [65995930],
  prod: [9219692]
};

// Track IDs to be ungated for testing under the general simulation gate (Academy:SimulationEnvironment)
//Hubspot Reporting, Email Marketing, Set up Hubspot CRM for Growth, Service Hub Software
const SIMULATION_TESTING_TRACKS = {
  qa: [1097271, 1097331],
  prod: [9181275, 9151241, 8984877, 9148739]
};
const SIMULATION_TESTING_GATE = 'Academy:SimulationEnvironment';
const MARKETING_HUB_SIMULATION_GATE = 'Academy:MarketingHubSimulation';

// Exercise IDs that are fully enabled (no gate)
// [Advance a Deal on Your Deal Board in HubSpot, Create a Task From a Contact Record, Book a Meeting Using the Meetings Tool]
const ENABLED_SIMULATION_EXERCISES = {
  qa: [22745667, 22745653, 22745655],
  prod: [8251321, 14672225, 8251397]
};

// Exercise IDs that should NOT open in simulation mode (blacklist)
const DISABLED_SIMULATION_EXERCISES = {
  qa: [],
  prod: [14756457]
};
export const useIsSimulationExercise = (exerciseId, trackId) => {
  const environment = enviro.isQa() ? 'qa' : 'prod';
  const isDisabledSimulationExercise = DISABLED_SIMULATION_EXERCISES[environment].includes(exerciseId);
  const isEnabledSimulationTrack = trackId ? ENABLED_SIMULATION_TRACKS[environment].includes(trackId) : false;
  const isEnabledSimulationExercise = ENABLED_SIMULATION_EXERCISES[environment].includes(exerciseId);
  const isMarketingHubTrack = trackId ? MARKETING_HUB_TRACKS[environment].includes(trackId) : false;
  const isSimulationTestingTrack = trackId ? SIMULATION_TESTING_TRACKS[environment].includes(trackId) : false;
  const {
    hasGate: hasMarketingHubGate,
    hasGateLoading: isMarketingHubGateLoading
  } = useHasGate(MARKETING_HUB_SIMULATION_GATE);
  const {
    hasGate: hasSimulationTestingGate,
    hasGateLoading: isSimulationTestingGateLoading
  } = useHasGate(SIMULATION_TESTING_GATE);
  const isSimulationExercise = useMemo(() => {
    if (isDisabledSimulationExercise) return false;
    if (isEnabledSimulationTrack) return true;
    if (isEnabledSimulationExercise) return true;
    if (!trackId) return false;
    if (isMarketingHubTrack) return hasMarketingHubGate === true;
    if (isSimulationTestingTrack) return hasSimulationTestingGate === true;
    return false;
  }, [isDisabledSimulationExercise, isEnabledSimulationTrack, isEnabledSimulationExercise, trackId, isMarketingHubTrack, isSimulationTestingTrack, hasMarketingHubGate, hasSimulationTestingGate]);
  const isGateLoading = isEnabledSimulationTrack || isEnabledSimulationExercise ? false : isMarketingHubGateLoading || isSimulationTestingGateLoading;
  return {
    isSimulationExercise,
    isSimulationGateLoading: isGateLoading
  };
};
export const usePracticalExercisePortalId = (exerciseId, trackId) => {
  const currentPortalId = PortalIdParser.getPortalIdFromPath();
  const {
    isSimulationExercise,
    isSimulationGateLoading
  } = useIsSimulationExercise(exerciseId, trackId);
  const {
    getOrCreateSimulationPortal,
    simulationPortal,
    simulationPortalLoading,
    simulationPortalError
  } = usePracticalExerciseSimulationPortal({
    variables: {
      exerciseId
    }
  });
  const getOrCreatePortal = useCallback(() => {
    if (isSimulationExercise) {
      return getOrCreateSimulationPortal().catch(error => {
        Raven.captureException(error);
      });
    }
  }, [isSimulationExercise, getOrCreateSimulationPortal]);
  return {
    getOrCreatePortal,
    portalId: isSimulationExercise ? (simulationPortal === null || simulationPortal === void 0 ? void 0 : simulationPortal.portalId) || currentPortalId : currentPortalId,
    portalIdLoading: simulationPortalLoading,
    portalIdError: simulationPortalError,
    isSimulationExercise,
    isSimulationGateLoading
  };
};